import { HStack, Icon, Flex, VStack, Button, Text, Center, Box, useToast } from '@cad-arc';
import classnames from 'classnames';
import { format, isSameDay, isSameYear, parseJSON } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useCallback } from 'react';
import { AdditionalInfoType, AdditionalInfoView, Priority, PRO_QA_TYPES } from '../types';
import { useAdditionalInfos } from '../hooks/use-additional-infos/additional-infos-provider';
import styles from './additional-info-row.module.css';
import { FlagRenderer } from '../../common/components/flag-renderer/flag-renderer';
import { useShowErrorAlert } from '../hooks/use-show-error-alert/use-show-error-alert';
import { colors } from '../../colors/colors';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { NarrativeActionMenu } from './narrative-action-menu';
import { NarrativeEditRow } from './narrative-edit-row';
import { DeleteNarrativeConfirmation } from './delete-narrative-confirmation';
import { useNarrativeRowActions } from '../hooks/use-narrative-row-actions/use-narrative-row-actions';
import { NarrativeRowNavProps } from '../hooks/use-narrative-keyboard-nav/use-narrative-keyboard-nav';
import { useNarrativeRowKeyboard } from '../hooks/use-narrative-row-keyboard/use-narrative-row-keyboard';

export const formatAdditionalInfoTime = (date: Date, now: Date): string => {
    // \xa0 = Non-breaking space
    if (isSameDay(date, now)) return format(date, 'HH:mm:ss');
    if (isSameYear(date, now)) return format(date, 'LL/dd\xa0HH:mm');
    return format(date, 'LL/dd/yyyy\xa0HH:mm');
};

const formatAdditionalInfoAuthor = (info: AdditionalInfoView): string => {
    return info.authorCallSign
        ? info.authorCallSign
        : info.userCadId
          ? `${info.userInitials}-\n${info.userCadId}`
          : info.userInitials;
};

export const isProQaInitOrEndMessage = (info: AdditionalInfoView): boolean => {
    return (
        info.type === AdditionalInfoType.PROQA &&
        !!info.text &&
        info.text.indexOf('<') >= 0 &&
        info.text.indexOf('>') >= 0
    );
};

const getInfoTextTestId = (info: AdditionalInfoView): string => {
    switch (info.type) {
        case AdditionalInfoType.USER:
            return 'ADDITIONAL_NARRATIVE';
        case AdditionalInfoType.SYSTEM:
            return 'ADDITIONAL_NARRATIVE_SYSTEM';
        case AdditionalInfoType.PROQA:
            return 'ADDITIONAL_NARRATIVE_PROQA';
        case AdditionalInfoType.PRO_QA_DESC_INFO:
            return 'ADDITIONAL_NARRATIVE_PROQA_DESC_INFO';
        case AdditionalInfoType.PRO_QA_COMMENT:
            return 'ADDITIONAL_NARRATIVE_PROQA_COMMENT';
        case AdditionalInfoType.PRO_QA_KEY_QUESTION:
            return 'ADDITIONAL_NARRATIVE_PROQA_KEY_Q';
        case AdditionalInfoType.PRO_QA_CRITICAL:
            return 'ADDITIONAL_NARRATIVE_PROQA_CRITICAL';
        case AdditionalInfoType.PRO_QA_SCRIPT:
            return 'ADDITIONAL_NARRATIVE_PROQA_SCRIPT';
        case AdditionalInfoType.PRO_QA_UPDATE:
            return 'ADDITIONAL_NARRATIVE_PROQA_UPDATE';
        case AdditionalInfoType.PRO_QA_URGENT:
            return 'ADDITIONAL_NARRATIVE_PROQA_URGENT';
        default:
            return '';
    }
};

// Re-export for backward compatibility — canonical definition is in ../../types

const UnreadLine = ({ isUrgent }: { isUrgent: boolean }) => {
    const color = isUrgent ? 'var(--arc-colors-urgent-default)' : colors.messagesUnreadLineNormal;
    return (
        <Center width="100%" height="var(--arc-space-4)">
            <Flex flexDirection="row" width="100%">
                <Center flexGrow={1}>
                    <Flex flexGrow={1} borderTop={`1px solid ${color}`} />
                </Center>
                <Box paddingY="var(--arc-space-1)">
                    <Text variant="headingXs" style={{ color }}>
                        {isUrgent ? 'Urgent' : 'New'}
                    </Text>
                </Box>
            </Flex>
        </Center>
    );
};

export const AdditionalInfoRow = observer(
    ({
        data: info,
        index,
        agencyEventNumberToIdMap,
        currentUserId,
        canDeleteInfos = true,
        isScheduledEvent = false,
        isBolo = false,
        onOpenEvent,
        isReadOnlyMode = false,
        isFirstResponder = false,
        focusNarrativeInput,
        rowNavProps,
        isPrevSiblingHighPriority = false,
        isNextSiblingHighPriority = false,
    }: {
        data: AdditionalInfoView;
        index: number;
        agencyEventNumberToIdMap?: Record<string, number>;
        currentUserId?: number;
        canDeleteInfos?: boolean;
        isScheduledEvent?: boolean;
        isBolo?: boolean;
        onOpenEvent?: (id: number) => void;
        isReadOnlyMode?: boolean;
        isFirstResponder?: boolean;
        focusNarrativeInput?: () => void;
        rowNavProps?: NarrativeRowNavProps;
        /** Whether the chronologically-adjacent row above/below is also
         *  high-priority — used to square the shared edge so consecutive
         *  urgent rows read as one merged block instead of each rounding
         *  all 4 corners independently. */
        isPrevSiblingHighPriority?: boolean;
        isNextSiblingHighPriority?: boolean;
    }) => {
        const { entityId, earliestUnreadInfo, entityNumber, focusedFlashId } = useAdditionalInfos();
        const { showFailedToDeleteAlert } = useShowErrorAlert();
        const toast = useToast();

        const { rollOutCadNarrativeFilteringAndFocusImprovementsEnabled } = useEnabledFeatures();

        const handleDeleteSuccess = useCallback(() => {
            if (rollOutCadNarrativeFilteringAndFocusImprovementsEnabled) {
                toast({ status: 'default', description: 'Narrative Message Deleted' });
                focusNarrativeInput?.();
            }
        }, [rollOutCadNarrativeFilteringAndFocusImprovementsEnabled, toast, focusNarrativeInput]);

        const handleDeleteError = useCallback(() => {
            showFailedToDeleteAlert(entityNumber);
        }, [showFailedToDeleteAlert, entityNumber]);

        const handleOpenMergedEvent = useCallback(() => {
            onOpenEvent?.(info.agencyEventId);
        }, [onOpenEvent, info.agencyEventId]);

        const {
            isEditing,
            isSaving,
            handleEdit,
            handleEditCancel,
            handleEditSave,
            isTogglingEmphasis,
            handleToggleEmphasis,
            isDeleting,
            isDeleteConfirmOpen,
            executeDelete,
            handleDelete,
            handleDeleteCancel,
            handlePin,
            handleUnpin,
            isPinning,
            isPinCapReached,
            pinCapTooltip,
            canShowActionMenu,
            isSelected,
            handleRowFocus,
            handleRowBlur,
            handleRowDeselect,
            handleRowMouseDown,
        } = useNarrativeRowActions({
            info,
            // AdditionalInfoRow is only ever the chronological-list row; the
            // pinned section renders PinnedNarrativeRow instead.
            isPinnedRow: false,
            returnFocusToRow: rowNavProps?.focusSelf,
            // Flag-on rows confirm before delete; the legacy flag-off trash icon
            // deletes immediately.
            shouldConfirmBeforeDelete: rollOutCadNarrativeFilteringAndFocusImprovementsEnabled,
            onDeleteSuccess: handleDeleteSuccess,
            onDeleteError: handleDeleteError,
        });

        const { handleMenuEscape, handleRowKeyDown, handleFocus, handleBlur } =
            useNarrativeRowKeyboard({
                rowNavProps,
                handleRowDeselect,
                handleRowFocus,
                handleRowBlur,
                focusNarrativeInput,
            });

        const isProQA = PRO_QA_TYPES.includes(info.type as AdditionalInfoType);
        const isProQAInitOrEndMsg = isProQaInitOrEndMessage(info);
        // A pinned row shows only the pin icon at the front — it takes precedence
        // over the priority (high-priority) and ProQA icons it would otherwise share
        // the slot with.
        const showPinIndicator =
            rollOutCadNarrativeFilteringAndFocusImprovementsEnabled && info.isPinned;
        const isMergedEventNarrative =
            info.parentAgencyEventId != null && info.parentAgencyEventId === entityId;
        const hasTrashIcon =
            !rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
            canDeleteInfos &&
            currentUserId &&
            currentUserId === info.authorUserId &&
            info.type === AdditionalInfoType.USER &&
            !info.isEventNarrative &&
            !isProQA;

        const isUnread =
            info.id !== undefined && // the scheduled event info doesn't have an id
            earliestUnreadInfo?.id === info.id;

        const isUrgent = info.priority === Priority.HIGH;
        const infoTypeSystem = info.type === AdditionalInfoType.SYSTEM;
        const isEmptyText = !info.text?.trim();

        // After a go-to-pinned click scrolls this row into view, flash its
        // background so the eye can find it. Only the chronological-list row
        // flashes — never the pinned-section copy (which renders elsewhere).
        const isFlashing =
            rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
            info.id !== undefined &&
            focusedFlashId === info.id;

        if (isDeleteConfirmOpen) {
            return (
                <DeleteNarrativeConfirmation
                    messageText={info.text}
                    isLoading={isDeleting}
                    isFirstResponder={isFirstResponder}
                    onConfirm={executeDelete}
                    onClose={handleDeleteCancel}
                />
            );
        }

        return (
            <VStack w="100%">
                <HStack
                    justify="start"
                    position="relative"
                    spacing={0}
                    ref={rowNavProps?.ref}
                    tabIndex={rowNavProps?.tabIndex}
                    onKeyDown={handleRowKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onMouseDown={handleRowMouseDown}
                    onClick={handleRowDeselect}
                    data-testid={
                        info.priority === Priority.HIGH
                            ? 'HIGH_PRIORITY_ADDITIONAL_NARRATIVE_ROW'
                            : 'ADDITIONAL_INFO_ROW'
                    }
                    data-index={index}
                    data-infoid={info?.id}
                    data-focus-flash={isFlashing ? 'true' : undefined}
                    className={classnames(styles['row'], {
                        [styles['is-editing']]: isEditing,
                        [styles['is-selected']]: isSelected,
                        [styles['high-priority']]: isUrgent,
                        [styles['is-proqa']]: isProQA,
                        [styles['user-is-author']]: currentUserId === info.authorUserId,
                        [styles['focus-flash']]: isFlashing,
                        [styles['lg']]: isFirstResponder,
                        [styles['is-empty-text']]: isEmptyText,
                        [styles['square-top']]: isUrgent && isPrevSiblingHighPriority,
                        [styles['square-bottom']]: isUrgent && isNextSiblingHighPriority,
                    })}
                >
                    <Box
                        align="center"
                        className={classnames(
                            styles[isFirstResponder ? 'row-icon-lg' : 'row-icon-md'],
                            {
                                [styles['is-deleting']]: isDeleting,
                            }
                        )}
                    >
                        {info.priority === Priority.HIGH && !showPinIndicator && (
                            <Icon
                                data-testid="HIGH_PRIORITY_ADDITIONAL_NARRATIVE_ROW_ICON"
                                className={styles['exclamation-point']}
                                icon="ExclamationPoint"
                            />
                        )}
                        {isProQAInitOrEndMsg && !showPinIndicator && (
                            <Icon icon="ProQa" className={styles['pro-qa']} />
                        )}
                        {showPinIndicator && (
                            <Icon
                                data-testid="ADDITIONAL_NARRATIVE_PINNED_ICON"
                                className={styles['pin-indicator']}
                                icon="Pin"
                            />
                        )}
                        {!rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
                            hasTrashIcon && (
                                <Button
                                    className={styles['trash']}
                                    onClick={handleDelete}
                                    isLoading={isDeleting}
                                    data-testid="DELETE_ADDITIONAL_NARRATIVE_ICON"
                                >
                                    <Icon icon="TrashCan" />
                                </Button>
                            )}
                    </Box>
                    <HStack justify="start" className={classnames(styles['row-text'])}>
                        <Text
                            data-testid={getInfoTextTestId(info)}
                            data-index={index}
                            as="div"
                            className={classnames(
                                styles['content'],
                                styles[isFirstResponder ? 'content-lg' : 'content-md'],
                                infoTypeSystem ? styles['system'] : '',
                                isFirstResponder && infoTypeSystem && styles['system-md'],
                                !isFirstResponder && infoTypeSystem && styles['system-sm'],
                                isProQAInitOrEndMsg ? styles['proqa-init-close'] : ''
                            )}
                        >
                            {isEditing ? (
                                <NarrativeEditRow
                                    info={info}
                                    agencyEventId={info.agencyEventId}
                                    isFirstResponder={isFirstResponder}
                                    onSave={handleEditSave}
                                    onCancel={handleEditCancel}
                                    isSaving={isSaving}
                                />
                            ) : (
                                <FlagRenderer
                                    text={info.text}
                                    agencyEventNumberToIdMap={agencyEventNumberToIdMap}
                                    isFirstResponder={isFirstResponder}
                                    onAgencyEventNumberClick={({ eventId }) => {
                                        onOpenEvent?.(eventId);
                                    }}
                                />
                            )}
                        </Text>

                        <Text
                            as="div"
                            fontFamily="mono"
                            variant="caption"
                            color="text.secondary"
                            lineHeight="none"
                            className={classnames(
                                styles['date'],
                                isFirstResponder ? styles['date-md'] : styles['date-sm']
                            )}
                        >
                            {formatAdditionalInfoTime(parseJSON(info.createdDateUtc), new Date())}
                        </Text>
                        <Text
                            as="div"
                            fontFamily="mono"
                            variant="caption"
                            lineHeight="none"
                            color="text.secondary"
                            className={classnames(
                                styles['author'],
                                isFirstResponder ? styles['author-md'] : styles['author-sm']
                            )}
                        >
                            {formatAdditionalInfoAuthor(info)}
                            {isMergedEventNarrative ? (
                                rollOutCadNarrativeFilteringAndFocusImprovementsEnabled ? (
                                    <div
                                        data-testid="PARENT_EVENT_LINK"
                                        className={styles['parent-event-link']}
                                    >
                                        #{info.agencyEventNumber.slice(-4)}
                                    </div>
                                ) : (
                                    <div
                                        data-testid="PARENT_EVENT_LINK"
                                        className={classnames(
                                            styles['parent-event-link'],
                                            styles['parent-event-link-clickable']
                                        )}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleOpenMergedEvent();
                                        }}
                                    >
                                        #{info.agencyEventNumber.slice(-4)}
                                        <Icon icon="OpenNewWindow" />
                                    </div>
                                )
                            ) : (
                                // Align author column correctly for merged events with a dummy min-width element.
                                // No, putting the min-width on styles['author'] doesn't work.
                                <div
                                    className={styles['parent-event-link']}
                                    style={{ height: '0px' }}
                                />
                            )}
                        </Text>
                    </HStack>
                    {rollOutCadNarrativeFilteringAndFocusImprovementsEnabled &&
                        canShowActionMenu && (
                            <NarrativeActionMenu
                                info={info}
                                currentUserId={currentUserId}
                                isPinned={info.isPinned}
                                isScheduledEvent={isScheduledEvent}
                                isBolo={isBolo}
                                isFirstResponder={isFirstResponder}
                                isMergedEventNarrative={isMergedEventNarrative}
                                readOnly={isReadOnlyMode}
                                onEdit={handleEdit}
                                onToggleEmphasis={handleToggleEmphasis}
                                onPin={handlePin}
                                onUnpin={handleUnpin}
                                onOpenMergedEvent={handleOpenMergedEvent}
                                onDelete={handleDelete}
                                isDeleting={isDeleting}
                                isTogglingEmphasis={isTogglingEmphasis}
                                isPinning={isPinning}
                                isPinDisabledByCap={isPinCapReached}
                                pinDisabledTooltip={pinCapTooltip}
                                onActionClick={handleRowDeselect}
                                onEscape={handleMenuEscape}
                            />
                        )}
                </HStack>
                {isUnread && <UnreadLine isUrgent={isUrgent} />}
            </VStack>
        );
    }
);
